import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

import { ApiService } from "../../../config";
import { handleToken } from "../../../utils/expiredTokenLogout";
import { handleError } from "../../../utils/parseApiReturn";
import type { AcessoPublico, CategoriaNoGoogle, ClienteNovo, ContaConectada, EstadoDeAcesso, PedidoDeAcesso, PerfilNoGoogle, PerfilQualidade, QualidadePorEmail, Reivindicacao, SituacaoNoGoogle } from "../types";

const auth = (token: string): AxiosRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
});

const getQualidadePorEmail = async (token: string): Promise<QualidadePorEmail[]> => {
  try {
    const result = await ApiService.get("/profile-quality/by-email", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
    return [];
  }
};

const getPerfis = async (
  token: string,
  filtros: { email?: string; estado?: EstadoDeAcesso },
): Promise<PerfilQualidade[]> => {
  try {
    const result = await ApiService.get("/profile-quality/profiles", {
      ...auth(token),
      params: filtros,
    });
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar os perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar os perfis.").message);
    return [];
  }
};

const marcarAcessoSolicitado = async (token: string, publicId: string): Promise<boolean> => {
  try {
    const result = await ApiService.put(
      `/profile-quality/${publicId}/acesso-solicitado`,
      {},
      auth(token),
    );
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao marcar o pedido de acesso.").message);
      return false;
    }
    toast.success("Marcado como acesso solicitado.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao marcar o pedido de acesso.").message);
    return false;
  }
};

const getContas = async (token: string): Promise<ContaConectada[]> => {
  try {
    const result = await ApiService.get("/profile-quality/accounts", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
    return [];
  }
};

const verificarConvites = async (token: string, userId: number): Promise<string | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/accounts/${userId}/invitations/refresh`, {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao consultar os convites.").message);
      return null;
    }
    return String(result.data?.data ?? "Convites verificados.");
  } catch (error) {
    toast.error(handleError(error, "Erro ao consultar os convites.").message);
    return null;
  }
};

const verificarTodosOsConvites = async (token: string): Promise<boolean> => {
  try {
    const result = await ApiService.post("/profile-quality/accounts/invitations/refresh-all", {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao iniciar a verificação.").message);
      return false;
    }
    toast.success("Verificação iniciada. Os convites aparecem aqui em alguns minutos.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao iniciar a verificação.").message);
    return false;
  }
};

const criarLinkDeAcesso = async (token: string, publicId: string): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/${publicId}/acesso/link`, {}, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
    return null;
  }
};

const criarLinkParaClienteNovo = async (token: string, cliente: ClienteNovo): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post("/profile-quality/acesso/link-novo", cliente, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(handleError(error, "Erro ao gerar o link de acesso.").message);
    return null;
  }
};

const getPedidosDeClientesNovos = async (token: string): Promise<PedidoDeAcesso[]> => {
  try {
    const result = await ApiService.get("/profile-quality/acesso/novos", auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      return [];
    }
    return data ?? [];
  } catch {
    return [];
  }
};

/** Busca no Google Places sem esconder o motivo quando o Google recusa (chave suspensa, cota...). */
const buscarNoGoogle = async (
  token: string,
  texto: string,
): Promise<{ lista: PerfilNoGoogle[]; erro: string | null }> => {
  try {
    const result = await ApiService.get(`/business-profile-advisor/places?name=${encodeURIComponent(texto)}`, auth(token));
    const { data, error, message } = result.data;
    if (error) return { lista: [], erro: message || "O Google não respondeu à busca." };
    return { lista: data?.profiles ?? [], erro: null };
  } catch (error) {
    return { lista: [], erro: mensagemDeErro(error, "O Google não respondeu à busca.") };
  }
};

const situacaoNoGoogle = async (
  token: string,
  dados: { placeId: string | null; name: string; address: string; managerUserId: number },
): Promise<SituacaoNoGoogle | null> => {
  try {
    const result = await ApiService.get("/profile-quality/acesso/situacao-no-google", { ...auth(token), params: dados });
    const { data, error, message } = result.data;
    if (error) return { encontrada: false, temDono: null, requestAdminRightsUri: null, googleLocationName: null, title: null, address: null, managerEmail: null, erro: message || "Não deu para consultar o Google." };
    return data ?? null;
  } catch (error) {
    return { encontrada: false, temDono: null, requestAdminRightsUri: null, googleLocationName: null, title: null, address: null, managerEmail: null, erro: mensagemDeErro(error, "Não deu para consultar o Google.") };
  }
};

const reivindicar = async (token: string, cliente: ClienteNovo): Promise<Reivindicacao | null> => {
  try {
    const result = await ApiService.post("/profile-quality/acesso/reivindicar", cliente, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      handleToken(message);
      toast.error(message || "Não deu para reivindicar a ficha.");
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(mensagemDeErro(error, "Não deu para reivindicar a ficha."));
    return null;
  }
};

const opcoesDeVerificacao = async (token: string, tokenDoPedido: string): Promise<Reivindicacao | null> => {
  try {
    const result = await ApiService.get(`/profile-quality/acesso/${tokenDoPedido}/verificacao/opcoes`, auth(token));
    const { data, error } = result.data;
    return error ? null : data ?? null;
  } catch {
    return null;
  }
};

const iniciarVerificacao = async (
  token: string,
  tokenDoPedido: string,
  dados: { method: string; phoneNumber?: string | null; emailAddress?: string | null },
): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/acesso/${tokenDoPedido}/verificacao/iniciar`, dados, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      toast.error(message || "Não deu para enviar o código.");
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(mensagemDeErro(error, "Não deu para enviar o código."));
    return null;
  }
};

const concluirVerificacao = async (token: string, tokenDoPedido: string, pin: string): Promise<PedidoDeAcesso | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/acesso/${tokenDoPedido}/verificacao/concluir`, { pin }, auth(token));
    const { data, error, message } = result.data;
    if (error) {
      toast.error(message || "O Google não aceitou o código.");
      return null;
    }
    return data ?? null;
  } catch (error) {
    toast.error(mensagemDeErro(error, "O Google não aceitou o código."));
    return null;
  }
};

const buscarCategorias = async (token: string, texto: string): Promise<CategoriaNoGoogle[]> => {
  if (!texto.trim()) return [];
  try {
    const result = await ApiService.get(`/business-profile-advisor/searchCategory?input=${encodeURIComponent(texto)}`, auth(token));
    return result.data.data ?? [];
  } catch {
    return [];
  }
};

const mensagemDeErro = (error: unknown, reserva: string): string => {
  const resposta = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return resposta?.message || handleError(error, reserva).message;
};

/** Rotas públicas da página em que o cliente concede o acesso: só o token do link identifica o pedido. */
export const acessoPerfilService = {
  consultar: async (tokenDoLink: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.get(`/acesso-perfil/${tokenDoLink}`);
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Link inválido." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível abrir este link.") };
    }
  },
  concluir: async (tokenDoLink: string, code: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.post(`/acesso-perfil/${tokenDoLink}/concluir`, { code });
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Não foi possível concluir." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível concluir.") };
    }
  },
  escolher: async (tokenDoLink: string, locationName: string): Promise<{ dados: AcessoPublico | null; erro: string | null }> => {
    try {
      const result = await ApiService.post(`/acesso-perfil/${tokenDoLink}/escolher`, { locationName });
      const { data, error, message } = result.data;
      return error ? { dados: null, erro: message || "Não foi possível concluir." } : { dados: data, erro: null };
    } catch (error) {
      return { dados: null, erro: mensagemDeErro(error, "Não foi possível concluir.") };
    }
  },
};

export const profileQualityService = {
  criarLinkDeAcesso,
  criarLinkParaClienteNovo,
  getPedidosDeClientesNovos,
  buscarNoGoogle,
  situacaoNoGoogle,
  reivindicar,
  opcoesDeVerificacao,
  iniciarVerificacao,
  concluirVerificacao,
  buscarCategorias,
  getQualidadePorEmail,
  getPerfis,
  marcarAcessoSolicitado,
  getContas,
  verificarConvites,
  verificarTodosOsConvites,
};
